/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { pushOneNightlife } from "../../../middlewares/sysNavigation/SysNavigation";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import {
  Modal,
  GoBack,
  MySearch,
  MyButton,
  Pagination,
  DashboardIntro,
} from "../../home/component/Component";
import { _TYPES_ } from "../utils/types";
import { _PAGE_SIZE_ } from "../../../middlewares/other/tools";
import { useGeoPositionContext } from "../utils/geoPosition";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import PromoteContent from "../utils/PromoteContent";
import { FaRegCheckSquare, FaRegSquare, FaRegTrashAlt } from "react-icons/fa";
import { RiRefreshLine } from "react-icons/ri";

/* ---------------- routes ---------------- */
const addRoute = "/data/nightlife/add";
const editRoute = "/data/nightlife/edit";
const doseeDataRoutes = "/dosee/data";

/* ---------------- helpers ---------------- */
const getIds = (list = []) =>
  list
    ?.map((it) => it?.nightlife_id)
    .filter((id) => id !== null && id !== undefined);

const Overview = () => {
  const {
    nightlifes,
    nightlifesCount,
    fetchDoSeeData,
    deleteOneContent,
  } = useDoSeeDataContext();

  const [nightlifesList, setNightlifesList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  const [localSearch, setLocalSearch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContentIds, setSelectedContentIds] = useState([]);

  const {
    country_id,
    entry_label,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const data_request_body = {
    country_id,
    first_level_id,
    second_level_id,
    third_level_id,
    fourth_level_id,
    content: _TYPES_.NIGHTLIFE,
  };

  /* ---------------- fetch & hydrate ---------------- */
  useEffect(() => {
    if (!nightlifes) fetchDoSeeData(data_request_body, page, pageSize);
    setNightlifesList(nightlifes || []);
  }, [nightlifes]);

  const reloadData = () => fetchDoSeeData(data_request_body, page, pageSize);

  /* ---------------- search (local) ---------------- */
  const SearchList = (e) => {
    const key = e.target.value?.toString().toLowerCase() ?? "";
    if (!Array.isArray(nightlifes)) return;

    if (!key) {
      setNightlifesList(nightlifes);
      return;
    }

    const filtered = nightlifes.filter((n) =>
      (n?.title || "").toString().toLowerCase().includes(key)
    );
    setNightlifesList(filtered);
  };

  const LocalData = useMemo(
    () => (localSearch?.length > 0 ? localSearch : nightlifesList) || [],
    [localSearch, nightlifesList]
  );

  /* Keep selections in sync with visible rows (so "Select All" is accurate) */
  useEffect(() => {
    setSelectedContentIds((prev) =>
      prev.filter((id) => getIds(LocalData).includes(id))
    );
  }, [LocalData]);

  /* ---------------- bulk selection ---------------- */
  const allVisibleIds = useMemo(() => getIds(LocalData), [LocalData]);
  const isAllSelected =
    allVisibleIds.length > 0 &&
    selectedContentIds.length === allVisibleIds.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      // Unselect all visible
      setSelectedContentIds((prev) =>
        prev.filter((id) => !allVisibleIds.includes(id))
      );
    } else {
      // Merge visible into selected (avoid duplicates)
      setSelectedContentIds((prev) => Array.from(new Set([...prev, ...allVisibleIds])));
    }
  };

  const toggleRow = (id) => {
    setSelectedContentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ---------------- bulk delete ---------------- */
  const deleteMany = async () => {
    if (!selectedContentIds.length) return;
    setLoading(true);
    for (let i = 0; i < selectedContentIds.length; i++) {
      await deleteOneContent({
        data_id: selectedContentIds[i],
        content: _TYPES_.NIGHTLIFE,
      });
    }
    setLoading(false);
    setSelectedContentIds([]);
    reloadData();
  };

  /* ---------------- UI tokens ---------------- */
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-5";

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        <div className="mb-3">
          <BreadCrumbComponent />
        </div>
        <div className="flex items-center justify-between mb-2">
          <GoBack props={{ route: doseeDataRoutes }} />
        </div>

        <div className="mb-4">
          <DashboardIntro props={{ intro: `Nightlife`, name: entry_label }} />
        </div>

        <div className={`${card} ${sectionPad} mb-4`}>
          <div className="flex flex-col gap-3">
            <MySearch
              type={_TYPES_.NIGHTLIFE}
              value={localSearch}
              showGlobalSearch={true}
              setValue={setLocalSearch}
              onChange={SearchList}
            />

            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <MyButton
                  className="text-slate-800 flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  onClick={toggleSelectAll}
                >
                  {isAllSelected ? (
                    <FaRegCheckSquare className="text-green-600 text-lg" />
                  ) : (
                    <FaRegSquare className="text-slate-800 text-lg" />
                  )}
                  <span className="text-sm">
                    {isAllSelected ? "Unselect All" : "Select All"}
                  </span>
                </MyButton>

                <MyButton
                  onClick={deleteMany}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedContentIds.length
                      ? "bg-red-50 border border-red-200 hover:bg-red-100"
                      : "bg-slate-100 border border-slate-200 opacity-60 cursor-not-allowed"
                    }`}
                >
                  {loading ? (
                    <RiRefreshLine className="text-slate-800 text-lg animate-spin" />
                  ) : (
                    <FaRegTrashAlt
                      className={`text-base ${selectedContentIds.length ? "text-red-600" : "text-slate-500"
                        }`}
                    />
                  )}
                  <span className="text-sm text-slate-800">
                    Delete {selectedContentIds.length} item
                    {selectedContentIds.length === 1 ? "" : "s"}
                  </span>
                </MyButton>
              </div>

              {country_id ? (
                <Link to={addRoute}>
                  <MyButton className="px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-600 text-white">
                    Add Nightlife
                  </MyButton>
                </Link>
              ) : null}
            </div>
          </div>
        </div>

        <div className={`${card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 w-28">
                    Select
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-600 w-64">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {LocalData && LocalData.length ? (
                  LocalData.map((nglife) => {
                    const id = nglife?.nightlife_id;
                    const selected = selectedContentIds.includes(id);
                    return (
                      <tr
                        key={id}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <MyButton
                            className="flex items-center"
                            onClick={() => toggleRow(id)}
                          >
                            {selected ? (
                              <FaRegCheckSquare className="text-green-600 text-lg" />
                            ) : (
                              <FaRegSquare className="text-slate-800 text-lg" />
                            )}
                          </MyButton>
                        </td>
                        <td className="px-4 py-3 text-slate-800">
                          {nglife?.title || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {nglife?.slug || "—"}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex justify-end">
                            <ActionButton
                              props={nglife}
                              reloadData={reloadData}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="px-4 py-6 text-slate-400">No results…</td>
                    <td className="px-4 py-6" />
                    <td className="px-4 py-6" />
                    <td className="px-4 py-6" />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total_items={nightlifesCount}
            callbackFunction={(p, size) => {
              fetchDoSeeData(data_request_body, p, size);
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 *  @author Ntavigwa Bashombe
 */
const ActionButton = ({ props, reloadData }) => {
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);
  const { nightlifes, deleteOneContent, updateOneContent } =
    useDoSeeDataContext();

  const [showModal, setShowModal] = useState(false);
  const [promote, setPromote] = useState(false);
  const [promoteLoader, setPromoteLoader] = useState(false);

  const nightlife_id = props?.nightlife_id;
  const nightlife_title = props?.title;
  const nightlife = props;

  const deleteOneNightlifeById = async (id) => {
    const { status } = await deleteOneContent({
      data_id: id,
      content: _TYPES_.NIGHTLIFE,
    });
    if (status === 200) reloadData();
  };

  const buildData = (object, position) => ({
    promoted: `${position}`,
    country_id: object?.country_id,
    description: object?.description,
    first_level_id: object?.first_level_id,
    fourth_level_id: object?.fourth_level_id,
    second_level_id: object?.second_level_id,
    slug: object?.slug,
    status: object?.status,
    third_level_id: object?.third_level_id,
    thumbnail: object?.thumbnail,
    title: object?.title,
  });

  const promotion = async (position) => {
    const previousPromoted = nightlifes?.find(
      (n) => n?.promoted === String(position)
    );
    setPromoteLoader(true);

    const { status } = await updateOneContent({
      data_id: nightlife?.nightlife_id,
      data: buildData(props, position),
      content: _TYPES_.NIGHTLIFE,
    });

    if (previousPromoted) {
      await updateOneContent({
        data_id: previousPromoted?.nightlife_id,
        data: buildData(previousPromoted, "no"),
        content: _TYPES_.NIGHTLIFE,
      });
    }

    if (status === 200) reloadData();
    setPromoteLoader(false);
  };

  const suspension = async (row) => {
    const data = {
      country_id: row?.country_id,
      description: row?.description,
      first_level_id: row?.first_level_id,
      fourth_level_id: row?.fourth_level_id,
      second_level_id: row?.second_level_id,
      slug: row?.slug,
      status: row?.status === 1 ? 0 : 1,
      third_level_id: row?.third_level_id,
      thumbnail: row?.thumbnail,
      title: row?.title,
    };

    const { status } = await updateOneContent({
      data_id: row?.nightlife_id,
      data,
      content: _TYPES_.NIGHTLIFE,
    });

    if (status === 200) reloadData();
  };

  return (
    <>
      <Modal
        props={{
          name: nightlife_title,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneNightlifeById(nightlife_id),
        }}
      />

      <PromoteContent
        visible={promote}
        setVisible={setPromote}
        loading={promoteLoader}
        ids={nightlifes
          ?.filter(
            (n) => !["yes", "no", null, undefined].includes(n?.promoted)
          )
          .map((n) => ({
            id: n?.nightlife_id,
            title: n?.title,
            position: parseInt(n?.promoted),
          }))}
        onClick={(position) => promotion(position)}
      />

      <div className="flex flex-row justify-end items-center gap-2">
        {isSuperAdmin ? (
          <>
            <MyButton
              title={nightlife?.status === 1 ? "Suspend" : "Unsuspend"}
              className={`px-3 py-2 rounded-lg text-white ${nightlife?.status === 1
                  ? "bg-slate-800 hover:bg-slate-700"
                  : "bg-amber-700 hover:bg-amber-600"
                }`}
              onClick={() => suspension(nightlife)}
            />
            <MyButton
              title={
                ["yes", "no", null, undefined].includes(nightlife?.promoted)
                  ? "Promote"
                  : "Unpromote"
              }
              className={`px-3 py-2 rounded-lg text-white ${["yes", "no", null, undefined].includes(nightlife?.promoted)
                  ? "bg-slate-800 hover:bg-slate-700"
                  : "bg-amber-700 hover:bg-amber-600"
                }`}
              onClick={() => setPromote(true)}
            />
          </>
        ) : null}

        <Link to={editRoute}>
          <MyButton
            title="Edit"
            className="px-3 py-2 rounded-lg bg-blue-700 hover:bg-blue-600 text-white"
            onClick={() => pushOneNightlife(nightlife)}
          />
        </Link>

        <MyButton
          title="Delete"
          className="px-3 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white"
          onClick={() => setShowModal(true)}
        />
      </div>
    </>
  );
};

export default Overview;
